import { useEffect, useState } from "react";
import { X, Cookie, Check } from "lucide-react";

const STORAGE_KEY = "gt_cookie_consent";

const CookieBanner = () => {
  const [state, setState] = useState("checking"); // checking | hidden | banner | prefs
  const [prefs, setPrefs] = useState({
    necessary: true,       // always on
    functional: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // small delay so the banner glides in after page load
        const t = setTimeout(() => setState("banner"), 800);
        return () => clearTimeout(t);
      }
      setState("hidden");
    } catch {
      setState("banner");
    }
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...choice, ts: new Date().toISOString() }));
    } catch {}
    setState("hidden");
  };

  const acceptAll = () => save({ necessary: true, functional: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, functional: false, analytics: false, marketing: false });
  const saveSelection = () => save(prefs);

  if (state === "checking" || state === "hidden") return null;

  return (
    <>
      {/* Backdrop only for prefs modal */}
      {state === "prefs" && (
        <div
          onClick={() => setState("banner")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,10,10,0.55)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            zIndex: 99,
            animation: "fadeUp 300ms ease",
          }}
        />
      )}

      <div
        role="dialog"
        aria-label="Cookie consent"
        data-testid="cookie-banner"
        style={{
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          zIndex: 100,
          background: "var(--bg-alt)",
          border: "1.5px solid var(--ink)",
          boxShadow: "0 24px 64px rgba(10,10,10,0.18)",
          animation: "fadeUp 500ms cubic-bezier(.2,.8,.2,1) both",
          maxWidth: state === "prefs" ? 680 : 1360,
          margin: "0 auto",
        }}
      >
        {state === "banner" && (
          <div style={{ padding: "24px 28px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 20,
                alignItems: "center",
              }}
              className="cookie-row"
            >
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 40, height: 40, flexShrink: 0, borderRadius: "50%",
                    background: "var(--green-bg)", color: "var(--green)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <Cookie size={18} />
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>Cookie notice</div>
                  <p style={{ color: "var(--ink)", fontSize: 14.5, lineHeight: 1.6, margin: 0, maxWidth: 720 }}>
                    We use cookies to operate this site, measure performance, and improve your experience. You can accept all, reject non-essential, or manage your preferences. See our <a href="/privacy" style={{ color: "var(--green)", textDecoration: "underline", textUnderlineOffset: 3 }}>privacy notice</a>.
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
                className="cookie-actions"
              >
                <button
                  onClick={() => setState("prefs")}
                  data-testid="cookie-prefs"
                  className="chip"
                  style={{ padding: "10px 18px", fontSize: 13, letterSpacing: 0 }}
                >
                  Manage preferences
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="cookie-reject"
                  className="chip"
                  style={{ padding: "10px 18px", fontSize: 13, letterSpacing: 0 }}
                >
                  Reject all
                </button>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept"
                  className="btn"
                  style={{ padding: "10px 20px", fontSize: 13 }}
                >
                  Accept all
                  <span className="arrow"><Check size={12} strokeWidth={3} /></span>
                </button>
              </div>
            </div>
          </div>
        )}

        {state === "prefs" && (
          <div style={{ padding: "32px 32px 28px", maxHeight: "80vh", overflowY: "auto" }} data-testid="cookie-prefs-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
              <div>
                <div className="eyebrow">Privacy preferences</div>
                <h3 className="display-sm" style={{ marginTop: 8 }}>Cookie preferences</h3>
              </div>
              <button
                onClick={() => setState("banner")}
                aria-label="Close"
                style={{ background: "transparent", border: "1px solid var(--line-2)", width: 36, height: 36, borderRadius: "50%", color: "var(--ink)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <X size={16} />
              </button>
            </div>

            <p style={{ color: "var(--muted)", fontSize: 14.5, lineHeight: 1.6, marginBottom: 24 }}>
              Control how Gravitas Technology Solutions uses cookies on this site. Strictly necessary cookies are always on.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 0, borderTop: "1px solid var(--line)" }}>
              {[
                { key: "necessary", t: "Strictly necessary", d: "Required for core site functionality (page routing, form submission). Cannot be disabled.", locked: true },
                { key: "functional", t: "Functional", d: "Remember your preferences (e.g., saved form state, language). Disabling may impact site behavior." },
                { key: "analytics", t: "Analytics & performance", d: "Aggregated usage data that helps us improve the site (page views, scroll depth). Never identifies individuals." },
                { key: "marketing", t: "Marketing", d: "Attribution and retargeting cookies. Off by default." },
              ].map((cat) => (
                <label
                  key={cat.key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 52px",
                    gap: 24,
                    padding: "20px 0",
                    borderBottom: "1px solid var(--line)",
                    cursor: cat.locked ? "not-allowed" : "pointer",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15.5, color: "var(--ink)" }}>
                      {cat.t} {cat.locked && <span style={{ color: "var(--muted)", fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.1em", marginLeft: 8 }}>ALWAYS ON</span>}
                    </div>
                    <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>{cat.d}</div>
                  </div>
                  <div
                    style={{
                      width: 44, height: 24, borderRadius: 100,
                      background: prefs[cat.key] ? "var(--green)" : "var(--line-2)",
                      position: "relative",
                      transition: "background 200ms ease",
                      opacity: cat.locked ? 0.55 : 1,
                    }}
                    onClick={(e) => {
                      if (cat.locked) return;
                      e.preventDefault();
                      setPrefs((p) => ({ ...p, [cat.key]: !p[cat.key] }));
                    }}
                    data-testid={`cookie-toggle-${cat.key}`}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 2,
                        left: prefs[cat.key] ? 22 : 2,
                        width: 20, height: 20, borderRadius: "50%",
                        background: "#fff",
                        transition: "left 200ms ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                      }}
                    />
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 28, justifyContent: "flex-end" }}>
              <button onClick={rejectAll} className="chip" data-testid="cookie-prefs-reject" style={{ padding: "10px 18px", fontSize: 13, letterSpacing: 0 }}>Reject all</button>
              <button onClick={saveSelection} className="btn" data-testid="cookie-prefs-save" style={{ padding: "10px 20px", fontSize: 13 }}>
                Save preferences
                <span className="arrow"><Check size={12} strokeWidth={3} /></span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CookieBanner;
